import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";
import { Helmet } from "react-helmet-async";
import {
  FaCheckCircle,
  FaLeaf,
  FaCertificate,
  FaBarcode,
  FaDownload,
  FaBoxOpen,
  FaShieldAlt,
  FaSeedling,
  FaHeart,
  FaAppleAlt,
  FaIceCream,
  FaBreadSlice,
  FaCoffee,
  FaUtensils,
  FaFire,
  FaCookieBite,
  FaQuestionCircle,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import {
  GiCoconuts,
  GiPineapple,
  GiFruitBowl,
  GiAbacus,
  Gi3DGlasses,
} from "react-icons/gi";

const OrganicCoconutProducts = () => {
  const { t } = useTranslation();
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const products = [
    {
      name: t("organicCoconutProducts.products.0.name"),
      shortName: t("organicCoconutProducts.products.0.shortName"),
      images: [
        "/images/products/coconut-smiles-naturals-pack.webp",
        "/images/products/coconut-smiles-naturals-closeup.webp",
      ],
      emoji: "🥥",
      icon: <GiCoconuts />,
      color: "from-white to-amber-100",
      textColor: "text-amber-900",
      appearance: t("organicCoconutProducts.products.0.appearance"),
      aroma: t("organicCoconutProducts.products.0.aroma"),
      flavour: t("organicCoconutProducts.products.0.flavour"),
      texture: t("organicCoconutProducts.products.0.texture"),
      ingredients: t("organicCoconutProducts.products.0.ingredients"),
      servingIdeas: [
        t("organicCoconutProducts.products.0.servingIdeas.0"),
        t("organicCoconutProducts.products.0.servingIdeas.1"),
        t("organicCoconutProducts.products.0.servingIdeas.2"),
        t("organicCoconutProducts.products.0.servingIdeas.3"),
      ],
      description: t("organicCoconutProducts.products.0.description"),
      type: "snack",
      dataSheet: "/images/DataSheet/coconut-smiles-naturals.jpg",
    },
    {
      name: t("organicCoconutProducts.products.1.name"),
      shortName: t("organicCoconutProducts.products.1.shortName"),
      images: [
        "/images/products/coconut-smiles-pineapple-pack.webp",
        "/images/products/coconut-smiles-pineapple-closeup.webp",
      ],
      emoji: "🍍",
      icon: <GiPineapple />,
      color: "from-yellow-200 to-yellow-400",
      textColor: "text-yellow-900",
      appearance: t("organicCoconutProducts.products.1.appearance"),
      aroma: t("organicCoconutProducts.products.1.aroma"),
      flavour: t("organicCoconutProducts.products.1.flavour"),
      texture: t("organicCoconutProducts.products.1.texture"),
      ingredients: t("organicCoconutProducts.products.1.ingredients"),
      servingIdeas: [
        t("organicCoconutProducts.products.1.servingIdeas.0"),
        t("organicCoconutProducts.products.1.servingIdeas.1"),
        t("organicCoconutProducts.products.1.servingIdeas.2"),
        t("organicCoconutProducts.products.1.servingIdeas.3"),
      ],
      description: t("organicCoconutProducts.products.1.description"),
      type: "snack",
      dataSheet: "/images/DataSheet/coconut-smiles-pineapple.jpg",
    },
    {
      name: t("organicCoconutProducts.products.2.name"),
      shortName: t("organicCoconutProducts.products.2.shortName"),
      images: [
        "/images/products/coconut-smiles-passionfruit-pack.webp",
        "/images/products/coconut-smiles-passionfruit-closeup.webp",
      ],
      emoji: "💜",
      icon: <GiFruitBowl />,
      color: "from-purple-200 to-pink-300",
      textColor: "text-purple-900",
      appearance: t("organicCoconutProducts.products.2.appearance"),
      aroma: t("organicCoconutProducts.products.2.aroma"),
      flavour: t("organicCoconutProducts.products.2.flavour"),
      texture: t("organicCoconutProducts.products.2.texture"),
      ingredients: t("organicCoconutProducts.products.2.ingredients"),
      servingIdeas: [
        t("organicCoconutProducts.products.2.servingIdeas.0"),
        t("organicCoconutProducts.products.2.servingIdeas.1"),
        t("organicCoconutProducts.products.2.servingIdeas.2"),
      ],
      description: t("organicCoconutProducts.products.2.description"),
      type: "snack",
      dataSheet: "/images/DataSheet/coconut-smiles-passionfruit.jpg",
    },
    {
      name: t("organicCoconutProducts.products.3.name"),
      shortName: t("organicCoconutProducts.products.3.shortName"),
      images: [
        "/images/products/coconut-smiles-cinnamon-pack.webp",
        "/images/products/coconut-smiles-cinnamon-closeup.webp",
      ],
      emoji: "🟤",
      icon: <GiAbacus />,
      color: "from-amber-300 to-orange-400",
      textColor: "text-amber-900",
      appearance: t("organicCoconutProducts.products.3.appearance"),
      aroma: t("organicCoconutProducts.products.3.aroma"),
      flavour: t("organicCoconutProducts.products.3.flavour"),
      texture: t("organicCoconutProducts.products.3.texture"),
      ingredients: t("organicCoconutProducts.products.3.ingredients"),
      servingIdeas: [
        t("organicCoconutProducts.products.3.servingIdeas.0"),
        t("organicCoconutProducts.products.3.servingIdeas.1"),
        t("organicCoconutProducts.products.3.servingIdeas.2"),
        t("organicCoconutProducts.products.3.servingIdeas.3"),
      ],
      description: t("organicCoconutProducts.products.3.description"),
      type: "snack",
      dataSheet: "/images/DataSheet/coconut-smiles-cinnamon.jpg",
    },
    {
      name: t("organicCoconutProducts.products.4.name"),
      shortName: t("organicCoconutProducts.products.4.shortName"),
      images: [
        "/images/products/coconut-aminos-bottle.webp",
        "/images/products/coconut-aminos-pour.webp",
      ],
      emoji: "🍶",
      icon: <Gi3DGlasses />,
      color: "from-amber-700 to-brown-600",
      textColor: "text-amber-100",
      appearance: t("organicCoconutProducts.products.4.appearance"),
      aroma: t("organicCoconutProducts.products.4.aroma"),
      flavour: t("organicCoconutProducts.products.4.flavour"),
      texture: t("organicCoconutProducts.products.4.texture"),
      ingredients: t("organicCoconutProducts.products.4.ingredients"),
      servingIdeas: [
        t("organicCoconutProducts.products.4.servingIdeas.0"),
        t("organicCoconutProducts.products.4.servingIdeas.1"),
        t("organicCoconutProducts.products.4.servingIdeas.2"),
        t("organicCoconutProducts.products.4.servingIdeas.3"),
        t("organicCoconutProducts.products.4.servingIdeas.4"),
      ],
      description: t("organicCoconutProducts.products.4.description"),
      volume: t("organicCoconutProducts.products.4.volume"),
      type: "condiment",
      dietTags: [
        t("organicCoconutProducts.products.4.dietTags.0"),
        t("organicCoconutProducts.products.4.dietTags.1"),
        t("organicCoconutProducts.products.4.dietTags.2"),
      ],
      dataSheet: "/images/DataSheet/coconut-aminos.jpg",
    },
  ];

  // Navigate images
  const nextImage = () => {
    setActiveImageIndex((prev) =>
      prev === products[activeProduct].images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setActiveImageIndex((prev) =>
      prev === 0 ? products[activeProduct].images.length - 1 : prev - 1
    );
  };

  const claims = [
    { icon: <FaSeedling />, text: t("organicCoconutProducts.claims.0.text") },
    { icon: <FaLeaf />, text: t("organicCoconutProducts.claims.1.text") },
    { icon: <FaShieldAlt />, text: t("organicCoconutProducts.claims.2.text") },
    { icon: <FaHeart />, text: t("organicCoconutProducts.claims.3.text") },
  ];

  const applications = [
    {
      icon: <FaAppleAlt />,
      text: t("organicCoconutProducts.applications.items.0.text"),
    },
    {
      icon: <FaCoffee />,
      text: t("organicCoconutProducts.applications.items.1.text"),
    },
    {
      icon: <FaIceCream />,
      text: t("organicCoconutProducts.applications.items.2.text"),
    },
    {
      icon: <FaCookieBite />,
      text: t("organicCoconutProducts.applications.items.3.text"),
    },
    {
      icon: <FaBreadSlice />,
      text: t("organicCoconutProducts.applications.items.4.text"),
    },
    {
      icon: <FaUtensils />,
      text: t("organicCoconutProducts.applications.items.5.text"),
    },
  ];

  const DownloadModal = () => (
    <AnimatePresence>
      {showDownloadModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Fixed */}
            <div className="bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 text-white p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold mb-1 flex items-center space-x-2">
                  <GiCoconuts className="text-3xl" />
                  <span>{t("organicCoconutProducts.downloadModal.title")}</span>
                </h3>
                <p className="text-amber-100 text-sm">
                  {t("organicCoconutProducts.downloadModal.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowDownloadModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-300"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Product List - Scrollable */}
            <div className="p-6 space-y-3 overflow-y-auto flex-1">
              {products.map((product, index) => (
                <motion.a
                  key={index}
                  href={product.dataSheet}
                  download
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-gray-700 dark:to-gray-600 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Product Icon */}
                    <div
                      className={`w-16 h-16 bg-gradient-to-br ${product.color} rounded-xl flex items-center justify-center text-4xl shadow-md flex-shrink-0 border-2 border-white dark:border-gray-600`}
                    >
                      {product.emoji}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                        {product.name}
                      </h4>
                      <div className="flex items-center space-x-3 mt-1 text-xs text-gray-600 dark:text-gray-400">
                        <span className="flex items-center space-x-1">
                          <FaSeedling className="text-amber-600" />
                          <span>
                            {product.type === "snack"
                              ? t(
                                  "organicCoconutProducts.downloadModal.snackLabel"
                                )
                              : t(
                                  "organicCoconutProducts.downloadModal.condimentLabel"
                                )}
                          </span>
                        </span>
                        <span className="w-px h-3 bg-gray-300 dark:bg-gray-500"></span>
                        <span className="flex items-center space-x-1">
                          <FaLeaf className="text-green-600" />
                          <span>
                            {t(
                              "organicCoconutProducts.downloadModal.organicLabel"
                            )}
                          </span>
                        </span>
                        {product.dietTags && (
                          <>
                            <span className="w-px h-3 bg-gray-300 dark:bg-gray-500"></span>
                            <span className="flex items-center space-x-1">
                              <FaHeart className="text-red-500" />
                              <span>{product.dietTags[0]}</span>
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t("organicCoconutProducts.downloadModal.productInfo")}
                      </p>
                    </div>
                  </div>

                  {/* Download Icon */}
                  <div className="bg-gradient-to-br from-amber-600 to-yellow-600 text-white rounded-full p-3 group-hover:from-amber-700 group-hover:to-yellow-700 group-hover:scale-110 transition-all duration-300 shadow-md flex-shrink-0 ml-4">
                    <FaDownload className="text-xl" />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const certifications = [
    {
      name: t("dehydratedFruits.certifications.brc.name"),
      image: "/images/certifications/BRC.webp",
      alt: t("dehydratedFruits.certifications.brc.name"),
      description: t("dehydratedFruits.certifications.brc.description"),
      icon: "🏆",
    },
    {
      name: t("dehydratedFruits.certifications.eu.name"),
      image: "/images/certifications/EU.webp",
      alt: t("dehydratedFruits.certifications.eu.name"),
      description: t("dehydratedFruits.certifications.eu.description"),
      icon: "🇪🇺",
    },
    {
      name: t("dehydratedFruits.certifications.usda.name"),
      image: "/images/certifications/USDA.webp",
      alt: t("dehydratedFruits.certifications.usda.name"),
      description: t("dehydratedFruits.certifications.usda.description"),
      icon: "🇺🇸",
    },
    {
      name: t("dehydratedFruits.certifications.jas.name"),
      image: "/images/certifications/JAS.webp",
      alt: t("dehydratedFruits.certifications.jas.name"),
      description: t("dehydratedFruits.certifications.jas.description"),
      icon: "🇯🇵",
    },
    {
      name: t("dehydratedFruits.certifications.naturland.name"),
      image: "/images/certifications/Naturland.webp",
      alt: t("dehydratedFruits.certifications.naturland.name"),
      description: t("dehydratedFruits.certifications.naturland.description"),
      icon: "🌱",
    },
    {
      name: t("dehydratedFruits.certifications.fairtrade.name"),
      image: "/images/certifications/FairTrade.webp",
      alt: t("dehydratedFruits.certifications.fairtrade.name"),
      description: t("dehydratedFruits.certifications.fairtrade.description"),
      icon: "🤝",
    },
    {
      name: t("dehydratedFruits.certifications.fda.name"),
      image: "/images/certifications/FDA.webp",
      alt: t("dehydratedFruits.certifications.fda.name"),
      description: t("dehydratedFruits.certifications.fda.description"),
      icon: "✅",
    },
  ];

  const faqs = [
    {
      question: t("organicCoconutProducts.faqs.0.question"),
      answer: t("organicCoconutProducts.faqs.0.answer"),
    },
    {
      question: t("organicCoconutProducts.faqs.1.question"),
      answer: t("organicCoconutProducts.faqs.1.answer"),
    },
    {
      question: t("organicCoconutProducts.faqs.2.question"),
      answer: t("organicCoconutProducts.faqs.2.answer"),
    },
    {
      question: t("organicCoconutProducts.faqs.3.question"),
      answer: t("organicCoconutProducts.faqs.3.answer"),
    },
    {
      question: t("organicCoconutProducts.faqs.4.question"),
      answer: t("organicCoconutProducts.faqs.4.answer"),
    },
  ];

  return (
    <>
      <SEO
        title="Organic Coconut Products | Coconut Smiles & Aminos from Sri Lanka"
        description="Premium organic coconut products from Sri Lanka. Coconut Smiles snacks in Natural, Pineapple, Passion Fruit & Cinnamon flavors. Coconut Aminos - gluten-free soy sauce alternative. 100% organic, no added sugar, HACCP certified."
        keywords="organic coconut snacks, coconut smiles, organic coconut strips, Ceylon coconut products, coconut aminos, gluten-free soy sauce alternative, organic coconut snacks Sri Lanka, vegan coconut snacks, paleo coconut snacks, keto coconut aminos, natural coconut strips, pineapple coconut snacks, passion fruit coconut, cinnamon coconut, organic condiments, clean label snacks, non-GMO coconut products, preservative-free snacks, Sri Lankan organic coconut, HACCP certified coconut products"
        image="https://worganaturals.com/images/products/organic-coconut-smiles-collection.webp"
        url="/products/organic-coconut-products"
        type="product"
        productSchema={true}
        category="Organic Snacks & Condiments"
        brand="Worga Naturals"
        availability="InStock"
        price="4.99"
        currency="USD"
        sku="OCP-SMILES-100"
      />

      {/* ========================================
          STRUCTURED DATA - RICH SNIPPETS
      ======================================== */}
      <Helmet>
        {/* ==========================================
            PRODUCT SCHEMA WITH VARIANTS
        ========================================== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Organic Coconut Products Collection",
            description:
              "Premium organic coconut products from Sri Lanka including Coconut Smiles (natural coconut snacks in multiple flavors) and Coconut Aminos (gluten-free soy sauce alternative). 100% organic, no added sugars, HACCP certified.",
            brand: {
              "@type": "Brand",
              name: "Worga Naturals",
              logo: "https://worganaturals.com/images/logo.png",
            },
            manufacturer: {
              "@type": "Organization",
              name: "Worga Naturals (Pvt) Ltd.",
              address: {
                "@type": "PostalAddress",
                streetAddress: "No. 152/G, Meepavita, Udamapitigama",
                addressLocality: "Dompe",
                postalCode: "11680",
                addressCountry: "LK",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "+94-11-1234567",
                contactType: "Customer Service",
                email: "info@worganaturals.com",
              },
            },
            image: [
              "https://worganaturals.com/images/products/coconut-smiles-natural.webp",
              "https://worganaturals.com/images/products/coconut-smiles-pineapple.webp",
              "https://worganaturals.com/images/products/coconut-smiles-passion-fruit.webp",
              "https://worganaturals.com/images/products/coconut-smiles-cinnamon.webp",
              "https://worganaturals.com/images/products/coconut-aminos-bottle.webp",
            ],
            category: "Organic Snacks & Condiments",
            offers: {
              "@type": "AggregateOffer",
              availability: "https://schema.org/InStock",
              priceCurrency: "USD",
              lowPrice: "4.99",
              highPrice: "7.99",
              offerCount: "5",
              url: "https://worganaturals.com/products/organic-coconut-products",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "243",
              bestRating: "5",
              worstRating: "1",
            },
            additionalProperty: [
              {
                "@type": "PropertyValue",
                name: "Certifications",
                value: "HACCP, GMP, EU Organic, USDA Organic, Fairtrade",
              },
              {
                "@type": "PropertyValue",
                name: "Origin",
                value: "Sri Lanka - Single Origin",
              },
              {
                "@type": "PropertyValue",
                name: "Pack Sizes",
                value: "100g retail, Bulk available",
              },
              {
                "@type": "PropertyValue",
                name: "Shelf Life",
                value: "12 months (Smiles), 24 months (Aminos)",
              },
              {
                "@type": "PropertyValue",
                name: "Dietary",
                value: "Vegan, Gluten-Free, Paleo, Keto-Friendly (Aminos)",
              },
            ],
            hasVariant: [
              {
                "@type": "Product",
                name: "Organic Coconut Smiles - Naturals",
                description:
                  "100% organic coconut strips with no added sugars or preservatives. Pure, tender-chewy texture.",
                sku: "OCP-SMILES-NAT-100",
                image:
                  "https://worganaturals.com/images/products/coconut-smiles-natural.webp",
                offers: {
                  "@type": "Offer",
                  price: "4.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/coconut-smiles-natural",
                },
                nutrition: {
                  "@type": "NutritionInformation",
                  servingSize: "30g",
                  calories: "150 kcal",
                  fatContent: "10g",
                  carbohydrateContent: "12g",
                  proteinContent: "2g",
                },
              },
              {
                "@type": "Product",
                name: "Organic Coconut Smiles - Pineapple Symphony",
                description:
                  "Organic coconut strips blended with natural pineapple. Sweet and tangy tropical fusion.",
                sku: "OCP-SMILES-PIN-100",
                image:
                  "https://worganaturals.com/images/products/coconut-smiles-pineapple.webp",
                offers: {
                  "@type": "Offer",
                  price: "5.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/coconut-smiles-pineapple",
                },
              },
              {
                "@type": "Product",
                name: "Organic Coconut Smiles - Passion Fruit Delight",
                description:
                  "Organic coconut strips with vibrant passion fruit. Exotic tropical flavor.",
                sku: "OCP-SMILES-PAS-100",
                image:
                  "https://worganaturals.com/images/products/coconut-smiles-passion-fruit.webp",
                offers: {
                  "@type": "Offer",
                  price: "5.49",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/coconut-smiles-passion-fruit",
                },
              },
              {
                "@type": "Product",
                name: "Organic Coconut Smiles - Cinnamon Kiss",
                description:
                  "Organic coconut strips infused with Ceylon cinnamon. Warm, comforting spice.",
                sku: "OCP-SMILES-CIN-100",
                image:
                  "https://worganaturals.com/images/products/coconut-smiles-cinnamon.webp",
                offers: {
                  "@type": "Offer",
                  price: "5.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/coconut-smiles-cinnamon",
                },
              },
              {
                "@type": "Product",
                name: "Organic Coconut Aminos",
                description:
                  "Gluten-free soy sauce alternative made from coconut nectar and vinegar. Rich umami flavor, keto and paleo friendly.",
                sku: "OCP-AMINOS-250",
                image:
                  "https://worganaturals.com/images/products/coconut-aminos-bottle.webp",
                offers: {
                  "@type": "Offer",
                  price: "7.99",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  url: "https://worganaturals.com/products/coconut-aminos",
                },
                additionalProperty: [
                  {
                    "@type": "PropertyValue",
                    name: "Volume",
                    value: "250ml",
                  },
                  {
                    "@type": "PropertyValue",
                    name: "Dietary",
                    value: "Keto-Friendly, Paleo-Friendly, Gluten-Free",
                  },
                ],
              },
            ],
          })}
        </script>

        {/* ==========================================
            FAQ SCHEMA - RICH SNIPPETS
        ========================================== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Are these products suitable for special diets?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes! All our coconut products are naturally vegan, gluten-free, and paleo-friendly. Coconut Aminos is also keto-friendly. They contain no added sugars, preservatives, or artificial ingredients.",
                },
              },
              {
                "@type": "Question",
                name: "What makes Coconut Aminos different from soy sauce?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Coconut Aminos is a gluten-free, soy-free alternative made from coconut nectar and vinegar. It has a slightly sweeter, less salty taste with rich umami flavor, making it perfect for those avoiding soy or gluten.",
                },
              },
              {
                "@type": "Question",
                name: "How should I store Coconut Smiles?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Store in a cool, dry place away from direct sunlight. After opening, reseal the package tightly to maintain freshness and chewy texture.",
                },
              },
              {
                "@type": "Question",
                name: "Can I order custom pack sizes or private label?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Absolutely! We offer 100g retail packs and bulk sizes per customer requirement. Private labeling and custom blends are available—contact our sales team for details.",
                },
              },
              {
                "@type": "Question",
                name: "Are the ingredients truly 100% organic?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Every ingredient is certified organic, from the coconut to the pineapple, passionfruit, and Ceylon cinnamon. We maintain full traceability from farm to package.",
                },
              },
            ],
          })}
        </script>

        {/* ==========================================
            BREADCRUMB SCHEMA
        ========================================== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://worganaturals.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Products",
                item: "https://worganaturals.com/products",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Organic Coconut Products",
                item: "https://worganaturals.com/products/organic-coconut-products",
              },
            ],
          })}
        </script>

        {/* ==========================================
            ORGANIZATION SCHEMA
        ========================================== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Worga Naturals",
            url: "https://worganaturals.com",
            logo: "https://worganaturals.com/images/logo.png",
            description:
              "Premium organic products from Sri Lanka. Certified organic coconut products, king coconut water, and natural preserves.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "No. 152/G, Meepavita, Udamapitigama",
              addressLocality: "Dompe",
              postalCode: "11680",
              addressCountry: "LK",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+94-11-1234567",
              contactType: "Customer Service",
              email: "info@worganaturals.com",
              availableLanguage: ["English", "Sinhala"],
            },
            sameAs: [
              "https://facebook.com/worganaturals",
              "https://instagram.com/worganaturals",
              "https://linkedin.com/company/worganaturals",
              "https://twitter.com/worganaturals",
            ],
          })}
        </script>

        {/* ==========================================
            ITEM LIST SCHEMA - PRODUCT COLLECTION
        ========================================== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Organic Coconut Products Collection",
            description:
              "Complete range of organic coconut products including snacks and condiments",
            numberOfItems: 5,
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@type": "Product",
                  name: "Organic Coconut Smiles - Naturals",
                  url: "https://worganaturals.com/products/coconut-smiles-natural",
                  image:
                    "https://worganaturals.com/images/products/coconut-smiles-natural.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@type": "Product",
                  name: "Organic Coconut Smiles - Pineapple Symphony",
                  url: "https://worganaturals.com/products/coconut-smiles-pineapple",
                  image:
                    "https://worganaturals.com/images/products/coconut-smiles-pineapple.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 3,
                item: {
                  "@type": "Product",
                  name: "Organic Coconut Smiles - Passion Fruit Delight",
                  url: "https://worganaturals.com/products/coconut-smiles-passion-fruit",
                  image:
                    "https://worganaturals.com/images/products/coconut-smiles-passion-fruit.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 4,
                item: {
                  "@type": "Product",
                  name: "Organic Coconut Smiles - Cinnamon Kiss",
                  url: "https://worganaturals.com/products/coconut-smiles-cinnamon",
                  image:
                    "https://worganaturals.com/images/products/coconut-smiles-cinnamon.webp",
                },
              },
              {
                "@type": "ListItem",
                position: 5,
                item: {
                  "@type": "Product",
                  name: "Organic Coconut Aminos",
                  url: "https://worganaturals.com/products/coconut-aminos",
                  image:
                    "https://worganaturals.com/images/products/coconut-aminos-bottle.webp",
                },
              },
            ],
          })}
        </script>

        {/* ==========================================
            VIDEO SCHEMA (if you have product videos)
        ========================================== */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: "How Organic Coconut Smiles Are Made",
            description:
              "Discover the journey of our organic coconut products from Sri Lankan farms to your table.",
            thumbnailUrl:
              "https://worganaturals.com/images/videos/coconut-smiles-thumbnail.webp",
            uploadDate: "2024-01-15",
            duration: "PT2M30S",
            contentUrl:
              "https://worganaturals.com/videos/coconut-smiles-production.mp4",
            embedUrl: "https://www.youtube.com/embed/YOUR_VIDEO_ID",
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        {/* Download Modal */}
        <DownloadModal />
        {/* Spacer for navbar */}
        <div className="h-24"></div>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full text-sm font-semibold shadow-lg">
                {t("organicCoconutProducts.hero.badge")}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">
                {t("organicCoconutProducts.hero.title.part1")}{" "}
              </span>
              <span className="text-amber-600 dark:text-amber-500">
                {t("organicCoconutProducts.hero.title.part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto mb-8">
              {t("organicCoconutProducts.hero.description")}
            </p>

            {/* Claims Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap justify-center gap-4"
            >
              {claims.map((claim, i) => (
                <div
                  key={i}
                  className="inline-flex items-center space-x-2 bg-white dark:bg-gray-800 rounded-full px-5 py-2 shadow-lg"
                >
                  <span className="text-amber-600 text-xl">{claim.icon}</span>
                  <span className="text-gray-700 dark:text-gray-300 font-medium text-sm">
                    {claim.text}
                  </span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* Applications Section */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
                {t("organicCoconutProducts.applications.title")}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {applications.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/30 transition-all duration-300"
                  >
                    <div className="text-3xl mb-2 flex justify-center">
                      {item.icon}
                    </div>
                    <p className="text-sm font-semibold">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Product Family */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("organicCoconutProducts.productFamily.title")}
            </h2>

            {/* Product Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {products.map((product, index) => (
                <button
                  key={index}
                  onClick={() => setActiveProduct(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    activeProduct === index
                      ? "bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-lg scale-105"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md"
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <span className="text-xl">{product.emoji}</span>
                    <span className="hidden md:inline">
                      {product.shortName}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {/* Active Product Card */}
            <motion.div
              key={activeProduct}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="grid md:grid-cols-2 gap-8">
                {/* Product Image Slider */}
                <div
                  className={`bg-gradient-to-br ${products[activeProduct].color} flex items-center justify-center p-12 md:p-16 relative overflow-hidden`}
                >
                  {/* Image Display */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeImageIndex}
                      initial={{ opacity: 0, scale: 0.9, x: 100 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.9, x: -100 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      className="w-full h-full flex items-center justify-center"
                    >
                      <img
                        src={products[activeProduct].images[activeImageIndex]}
                        alt={products[activeProduct].name}
                        className="max-w-full max-h-[500px] object-contain drop-shadow-2xl rounded-lg"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `<div class="text-9xl">${products[activeProduct].emoji}</div>`;
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation Arrows */}
                  {products[activeProduct].images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t(
                          "organicCoconutProducts.imageNav.previous"
                        )}
                      >
                        <FaChevronLeft className="text-amber-600 text-2xl" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 backdrop-blur-sm z-10"
                        aria-label={t("organicCoconutProducts.imageNav.next")}
                      >
                        <FaChevronRight className="text-amber-600 text-2xl" />
                      </button>
                    </>
                  )}

                  {/* Image Indicators */}
                  {products[activeProduct].images.length > 1 && (
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/30 backdrop-blur-md rounded-full px-4 py-2 z-10">
                      {products[activeProduct].images.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveImageIndex(index)}
                          className={`transition-all duration-300 rounded-full ${
                            activeImageIndex === index
                              ? "bg-white w-10 h-3"
                              : "bg-white/50 hover:bg-white/75 w-3 h-3"
                          }`}
                          aria-label={t(
                            "organicCoconutProducts.imageNav.goTo",
                            { number: index + 1 }
                          )}
                        />
                      ))}
                    </div>
                  )}

                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold z-10">
                    {t("organicCoconutProducts.imageNav.counter", {
                      current: activeImageIndex + 1,
                      total: products[activeProduct].images.length,
                    })}
                  </div>

                  {/* Floating particles - Coconut themed */}
                  <motion.div
                    animate={{
                      y: [0, -100],
                      x: [0, 20],
                      opacity: [0.8, 0],
                    }}
                    transition={{ duration: 3, repeat: Infinity, delay: 0 }}
                    className="absolute bottom-20 left-20 text-4xl opacity-50"
                  >
                    ✨
                  </motion.div>
                  <motion.div
                    animate={{
                      y: [0, -120],
                      x: [0, -30],
                      opacity: [0.6, 0],
                    }}
                    transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
                    className="absolute bottom-32 right-24 text-3xl opacity-40"
                  >
                    🌴
                  </motion.div>
                  <motion.div
                    animate={{
                      y: [0, -90],
                      x: [0, 15],
                      opacity: [0.7, 0],
                    }}
                    transition={{ duration: 2.8, repeat: Infinity, delay: 1 }}
                    className="absolute top-32 left-32 text-3xl opacity-40"
                  >
                    🥥
                  </motion.div>
                </div>

                {/* Product Details */}
                <div className="p-8 md:p-12">
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {t(
                        "organicCoconutProducts.productDetails.badges.organic"
                      )}
                    </span>
                    <span className="px-4 py-1 rounded-full text-sm font-bold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                      {products[activeProduct].type === "snack"
                        ? t(
                            "organicCoconutProducts.productDetails.badges.snack"
                          )
                        : t(
                            "organicCoconutProducts.productDetails.badges.condiment"
                          )}
                    </span>
                  </div>

                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                    {products[activeProduct].name}
                  </h3>

                  {/* Diet Tags for Coconut Aminos */}
                  {products[activeProduct].dietTags && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {products[activeProduct].dietTags.map((tag, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full text-xs font-bold"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Description */}
                  <div className="bg-amber-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed italic">
                      "{products[activeProduct].description}"
                    </p>
                  </div>

                  {/* Sensory Profile */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("organicCoconutProducts.productDetails.appearance")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].appearance}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("organicCoconutProducts.productDetails.aroma")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].aroma}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("organicCoconutProducts.productDetails.flavour")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].flavour}
                      </span>
                    </div>
                    <div>
                      <span className="font-bold text-amber-600">
                        {t("organicCoconutProducts.productDetails.texture")}:
                      </span>
                      <span className="text-gray-700 dark:text-gray-300 ml-2">
                        {products[activeProduct].texture}
                      </span>
                    </div>
                  </div>

                  {/* Ingredients */}
                  <div className="bg-green-50 dark:bg-gray-700 rounded-xl p-6 mb-6">
                    <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">
                      {t("organicCoconutProducts.productDetails.ingredients")}:
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">
                      {products[activeProduct].ingredients}
                    </p>
                  </div>

                  {/* Serving Ideas */}
                  <div className="mb-6">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      {products[activeProduct].type === "snack"
                        ? t(
                            "organicCoconutProducts.productDetails.servingIdeas"
                          )
                        : t("organicCoconutProducts.productDetails.uses")}
                      :
                    </h4>
                    <div className="grid grid-cols-1 gap-3">
                      {products[activeProduct].servingIdeas.map((idea, i) => (
                        <div
                          key={i}
                          className="flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-3"
                        >
                          <FaCheckCircle className="text-amber-600 flex-shrink-0" />
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {idea}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Common Specs */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "organicCoconutProducts.productDetails.specs.origin"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "organicCoconutProducts.productDetails.specs.originValue"
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "organicCoconutProducts.productDetails.specs.packSize"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {products[activeProduct].volume ||
                            t(
                              "organicCoconutProducts.productDetails.specs.packSizeValue"
                            )}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {t(
                            "organicCoconutProducts.productDetails.specs.storage"
                          )}
                          :
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {t(
                            "organicCoconutProducts.productDetails.specs.storageValue"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Quality & Safety */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                {t("organicCoconutProducts.quality.title")}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    icon: <FaShieldAlt />,
                    title: t("organicCoconutProducts.quality.items.0.title"),
                    desc: t("organicCoconutProducts.quality.items.0.desc"),
                  },
                  {
                    icon: <FaBarcode />,
                    title: t("organicCoconutProducts.quality.items.1.title"),
                    desc: t("organicCoconutProducts.quality.items.1.desc"),
                  },
                  {
                    icon: <FaCertificate />,
                    title: t("organicCoconutProducts.quality.items.2.title"),
                    desc: t("organicCoconutProducts.quality.items.2.desc"),
                  },
                  {
                    icon: <FaBoxOpen />,
                    title: t("organicCoconutProducts.quality.items.3.title"),
                    desc: t("organicCoconutProducts.quality.items.3.desc"),
                  },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg text-center hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="text-5xl text-amber-600 mb-4 flex justify-center">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Certifications */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("dehydratedFruits.certifications.title")}
            </h2>

            <div className="flex flex-wrap justify-center items-center gap-6 max-w-5xl mx-auto">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  className="group"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div className="w-16 h-16 flex items-center justify-center bg-orange-50 dark:bg-gray-700 rounded-xl p-2 group-hover:bg-orange-100 dark:group-hover:bg-gray-600 transition-colors">
                      <img
                        src={cert.image}
                        alt={cert.alt}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const fallback = document.createElement("span");
                          fallback.className = "text-2xl";
                          fallback.textContent = cert.icon;
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center max-w-[80px]">
                      {cert.name}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Coconut Smiles Highlight */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 rounded-3xl p-8 md:p-12 text-white shadow-2xl overflow-hidden relative"
          >
            {/* Decorative coconuts */}
            <div className="absolute top-4 right-4 text-6xl opacity-20">🥥</div>
            <div className="absolute bottom-4 left-4 text-6xl opacity-20">
              🌴
            </div>

            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-center">
                {t("organicCoconutProducts.whyChoose.title")}
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <FaHeart className="mr-3" />{" "}
                    {t("organicCoconutProducts.whyChoose.pureSimple.title")}
                  </h3>
                  <p className="text-lg leading-relaxed">
                    {t(
                      "organicCoconutProducts.whyChoose.pureSimple.description"
                    )}
                  </p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <FaSeedling className="mr-3" />{" "}
                    {t("organicCoconutProducts.whyChoose.guiltFree.title")}
                  </h3>
                  <p className="text-lg leading-relaxed">
                    {t(
                      "organicCoconutProducts.whyChoose.guiltFree.description"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Coconut Aminos Spotlight */}
        <section className="bg-gradient-to-br from-amber-900 to-brown-800 py-16 text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto text-center"
            >
              <div className="text-7xl mb-6">🍶</div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                {t("organicCoconutProducts.aminosSpotlight.title")}
              </h2>
              <p className="text-xl leading-relaxed mb-8 max-w-3xl mx-auto">
                {t("organicCoconutProducts.aminosSpotlight.description")}
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: <FaUtensils />,
                    text: t(
                      "organicCoconutProducts.aminosSpotlight.uses.0.text"
                    ),
                  },
                  {
                    icon: <FaFire />,
                    text: t(
                      "organicCoconutProducts.aminosSpotlight.uses.1.text"
                    ),
                  },
                  {
                    icon: <FaCookieBite />,
                    text: t(
                      "organicCoconutProducts.aminosSpotlight.uses.2.text"
                    ),
                  },
                ].map((use, i) => (
                  <div
                    key={i}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6"
                  >
                    <div className="text-4xl mb-3">{use.icon}</div>
                    <p className="font-semibold text-lg">{use.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQs */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
              {t("organicCoconutProducts.faqsTitle")}
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-amber-50 dark:hover:bg-gray-700 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-4">
                      <FaQuestionCircle className="text-amber-600 text-2xl flex-shrink-0" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {faq.question}
                      </h3>
                    </div>
                    {openFaq === index ? (
                      <FaChevronUp className="text-amber-600 text-xl flex-shrink-0" />
                    ) : (
                      <FaChevronDown className="text-amber-600 text-xl flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed pl-10">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Technical Specs Teaser */}
        <section className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl"
          >
            <div className="text-center">
              <FaDownload className="text-6xl mb-4 mx-auto" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {t("organicCoconutProducts.technical.title")}
              </h2>
              <p className="text-lg mb-6 opacity-90">
                {t("organicCoconutProducts.technical.description")}
              </p>
              <button
                onClick={() => setShowDownloadModal(true)}
                className="bg-white text-indigo-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center space-x-2 mx-auto"
              >
                <FaDownload />
                <span>
                  {t("organicCoconutProducts.technical.downloadButton")}
                </span>
              </button>
            </div>
          </motion.div>
        </section>

        {/* Bottom Spacer */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default OrganicCoconutProducts;
